<template>
  <div v-if="isPrint" class="print-input">{{ time }}</div>
  <div v-else style="display: flex; position: relative" class="time-selector-wrapper">
    <!-- Mobile: Use input-formatter -->
    <input-formatter
      :format="enableSeconds ? 'HH:mm:ss' : 'HH:mm'"
      :readonly="readonly"
      :minTime="minTime"
      :maxTime="maxTime"
      v-model="time"
      @focusin="onFocusin"
      @focusout="onFocusout"
    />
    <vs-icon
      v-if="!readonly && !time"
      icon-pack="feather"
      icon="icon-clock"
      class="time-icon"
      @click="focusPicker"
    ></vs-icon>
    <vs-icon
      v-if="!readonly && time"
      icon-pack="feather"
      icon="icon-x"
      class="clear-icon"
      @click.stop="!readonly && clickXIcon()"
    ></vs-icon>
  </div>
</template>

<script>
import InputFormatter from "@/components/InputFormatter.vue";

export default {
  name: "TimeSelector",
  components: {
    InputFormatter,
  },
  props: {
    readonly: Boolean,
    value: {
      type: String,
      default: "",
    },
    isPrint: {
      type: Boolean,
      default: false,
    },
    minTime: { type: String, default: "" },
    maxTime: { type: String, default: "" },
    defaultHour: String,
    defaultMinute: String,
    enableSeconds: { type: Boolean, default: false },
  },
  data() {
    return {
      time: this.value,
      isMobileDevice: false,
      configDateTimePicker: {
        enableTime: true,
        enableSeconds: this.enableSeconds,
        noCalendar: true,
        time_24hr: true,
        minTime: this.minTime,
        maxTime: this.maxTime,
        defaultHour: this.defaultHour || '12',
        defaultMinute: this.defaultMinute || '00',
        onClose: [this.onFocusout],
        allowInput: true,
        disableMobile: true,
        onChange: [this.handleChange],
        onKeyDown: [this.handleKeyDown],
      },
    };
  },
  mounted() {
    this.checkDeviceType();
  },
  watch: {
    value(data) {
      this.time = data;
    },
    time(data) {
      this.$emit("input", data);
    },
    // 필요한 prop 변경 시 flatpickr 설정 갱신
    minTime(newVal) {
      this.updateFlatpickrOption('minTime', newVal);
    },
    maxTime(newVal) {
      this.updateFlatpickrOption('maxTime', newVal);
    }
  },
  methods: {
    checkDeviceType() {
      this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    updateFlatpickrOption(option, value) {
      if (this.$refs.flatPickr && this.$refs.flatPickr.fp) {
        this.$refs.flatPickr.fp.set(option, value);
      }
    },

    focusPicker() {
      const input = this.$el.querySelector('input');
      if (input) input.focus();
    },

    change() {
      // placeholder가 제대로 보이도록 지연 처리
      this.$nextTick(() => {
        const input = this.$refs.flatPickr.$el;
        if (input && !this.time) {
          input.placeholder = this.enableSeconds ? 'HH:MM:SS' : 'HH:MM';
        }
      });
    },

    clickXIcon() {
      // 1. Vue 모델 값 초기화
      this.time = "";

      // 2. Flatpickr 인스턴스 확인 및 초기화
      if (this.$refs.flatPickr && this.$refs.flatPickr.fp) {
        // flatpickr의 clear 메서드 사용: 선택된 날짜/시간을 지우고 입력 필드를 초기화
        this.$refs.flatPickr.fp.clear();

        // clear 메서드가 onChange를 트리거하지 않을 수 있으므로 명시적으로 이벤트 발생
        this.$emit("input", "");
        this.$emit("change", "");

        // 3. 입력 필드 및 플레이스홀더 처리 (clear()가 완벽하지 않을 경우 대비)
        this.$nextTick(() => {
          const input = this.$refs.flatPickr.$el;
          if (input) {
            input.value = ""; // 입력 필드 값을 확실히 비움
            input.placeholder = this.enableSeconds ? 'HH:MM:SS' : 'HH:MM'; // 플레이스홀더 설정
          }
          // flatpickr 팝업이 열려있다면 닫기
          this.$refs.flatPickr.fp.close();
        });
      } else {
        // flatpickr 인스턴스가 없는 경우에도 이벤트 발생
        this.$emit("input", "");
        this.$emit("change", "");
      }
    },
    onFocusin () {
      console.log("TimeSelector focused");
      this.$emit("focusin");
    },
    onFocusout() {
      this.$emit("focusout");
    },

    handleChange(selectedDates, dateStr) {
      // 선택된 시간이 없으면 빈 문자열로 처리
      if (!dateStr || dateStr.trim() === '') {
        this.time = "";
        this.$emit("input", "");
        this.$emit("change", "");
        return;
      }

      // 시간 포맷 처리
      let formattedTime = dateStr;
      if (dateStr.includes(':')) {
        const timeParts = dateStr.split(':');
        if (timeParts.length >= 2) {
          // 시간과 분을 처리
          let hours = timeParts[0].padStart(2, '0');
          let minutes = timeParts[1].padStart(2, '0');

          // 시간 범위 검증 (0-23)
          const hoursNum = parseInt(hours, 10);
          if (hoursNum >= 0 && hoursNum < 24) {
            hours = hoursNum.toString().padStart(2, '0');
          } else {
            hours = '00';
          }

          // 분 범위 검증 (0-59)
          const minutesNum = parseInt(minutes, 10);
          if (minutesNum >= 0 && minutesNum < 60) {
            minutes = minutesNum.toString().padStart(2, '0');
          } else {
            minutes = '00';
          }

          if (this.enableSeconds && timeParts.length >= 3) {
            // 초 처리
            let seconds = timeParts[2].padStart(2, '0');
            const secondsNum = parseInt(seconds, 10);
            if (secondsNum >= 0 && secondsNum < 60) {
              seconds = secondsNum.toString().padStart(2, '0');
            } else {
              seconds = '00';
            }
            formattedTime = `${hours}:${minutes}:${seconds}`;
          } else {
            formattedTime = `${hours}:${minutes}`;
          }

          // minTime과 maxTime 범위 체크
          if (this.minTime && this.maxTime) {
            const currentTime = formattedTime;
            const [currentHours, currentMinutes] = currentTime.split(':').map(Number);
            const [minHours, minMinutes] = this.minTime.split(':').map(Number);
            const [maxHours, maxMinutes] = this.maxTime.split(':').map(Number);

            const currentTotalMinutes = currentHours * 60 + currentMinutes;
            const minTotalMinutes = minHours * 60 + minMinutes;
            const maxTotalMinutes = maxHours * 60 + maxMinutes;

            // minTime이 maxTime보다 큰 경우 (자정을 걸치는 경우)
            if (minTotalMinutes > maxTotalMinutes) {
              if (currentTotalMinutes < minTotalMinutes && currentTotalMinutes > maxTotalMinutes) {
                // 유효하지 않은 범위인 경우 minTime으로 조정
                formattedTime = this.minTime;
              }
            } else {
              // 일반적인 경우
              if (currentTime < this.minTime) {
                formattedTime = this.minTime;
              } else if (currentTime > this.maxTime) {
                formattedTime = this.maxTime;
              }
            }
          }
        }
      }

      this.time = formattedTime;
      this.$emit("input", formattedTime);
      this.$emit("change", formattedTime);
    },

    handleKeyDown(event) {
      if (event.key === 'Enter') {
        const input = this.$refs.flatPickr.$el;
        if (input && input.value) {
          // 키보드 입력값을 handleChange 메서드로 처리
          this.handleChange(null, input.value);
        }
      }

      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
        e.preventDefault(); // 숫자 외 키 입력 막기
      }
    },

    handleTimeElementInput() {
      // 입력 후 약간의 딜레이를 두고 처리하여 flatpickr 내부 처리가 완료되도록 함
      setTimeout(() => {
        if (this.$refs.flatPickr && this.$refs.flatPickr.fp) {
          // 시간 요소 값 가져오기
          let hours = this.$refs.flatPickr.fp.hourElement ? this.$refs.flatPickr.fp.hourElement.value : '00';
          let minutes = this.$refs.flatPickr.fp.minuteElement ? this.$refs.flatPickr.fp.minuteElement.value : '00';
          let seconds = this.$refs.flatPickr.fp.secondElement ? this.$refs.flatPickr.fp.secondElement.value : '00';

          // 숫자 입력시 올바른 값으로 변환 (예: '1' -> '01', '04' -> '04')
          hours = hours.padStart(2, '0');
          minutes = minutes.padStart(2, '0');
          seconds = seconds.padStart(2, '0');

          // 2자리 이상 입력된 경우 처리 (예: '14' -> '14' 그대로 유지)
          if (hours.length > 2) {
            // 시간이 24시 형식에 맞게 처리
            const hoursNum = parseInt(hours, 10);
            if (hoursNum >= 0 && hoursNum < 24) {
              hours = hoursNum.toString().padStart(2, '0');
            } else {
              hours = '12'; // 유효하지 않은 시간은 기본값으로
            }
          }

          const timeString = this.enableSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;

          this.time = timeString;
          this.$emit("input", timeString);
          this.$emit("change", timeString);
        }
      }, 100);
    },

    handleDocumentClick(e) {
      // flatpickr 외부 클릭 시 현재 입력값 저장
      if (this.$refs.flatPickr && this.$refs.flatPickr.fp) {
        const flatpickrElement = this.$refs.flatPickr.$el;
        const calendar = document.querySelector('.flatpickr-calendar');

        if (flatpickrElement && calendar && !flatpickrElement.contains(e.target) && !calendar.contains(e.target)) {
          this.handleTimeElementInput();
        }
      }
    },

    beforeDestroy() {
      // 컴포넌트 제거 시 이벤트 리스너 제거
      document.removeEventListener('mousedown', this.handleDocumentClick);

      if (this.$refs.flatPickr && this.$refs.flatPickr.fp) {
        if (this.$refs.flatPickr.fp.hourElement) {
          this.$refs.flatPickr.fp.hourElement.removeEventListener('input', this.handleTimeElementInput);
        }
        if (this.$refs.flatPickr.fp.minuteElement) {
          this.$refs.flatPickr.fp.minuteElement.removeEventListener('input', this.handleTimeElementInput);
        }
        if (this.$refs.flatPickr.fp.secondElement) {
          this.$refs.flatPickr.fp.secondElement.removeEventListener('input', this.handleTimeElementInput);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/*.time-selector-wrapper {
  width: 100%;
}*/

.time-icon, .clear-icon {
  color: #3c3c3c;
  margin-top: 6px;
  margin-bottom: 6px;
  margin-right: 1px;
  padding-left: 6px;
  padding-right: 6px;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  font-size: 24px;
  border-left: 1px solid #cdcdcd;
  background-color: #fff;
}

.flat::v-deep {
  background: white;

  input {
    /* 모바일에서도 플레이스홀더가 잘 보이도록 */
    &::placeholder {
      color: #999;
      opacity: 1;
    }
  }
}

.flat.readonly::v-deep {
  background: #fafafa;
}

/* 모바일에서 flatpickr 스타일 보정 */
@media (max-width: 767px) {
  .flatpickr-calendar {
    width: 100% !important;
    max-width: 307px !important;
  }

  .flatpickr-time {
    height: 40px !important;
    line-height: 40px !important;
    max-height: 40px !important;
  }

  .flatpickr-hour, .flatpickr-minute, .flatpickr-second {
    height: 38px !important;
    line-height: 38px !important;
  }
}
</style>

<style>
input[type="time"]::-webkit-calendar-picker-indicator {
  background: none;
}

/* flatpickr 모바일 스타일 보정 - 전역 스타일 */
.flatpickr-calendar.open {
  z-index: 999999 !important;
}
</style>
